/**
 * ClassWideGapsRollup — Wireframe v1, Wireframe 3 of 3.
 *
 * Collapsed-by-default strip below the Assessment Status table.
 * - When 0 topics are flagged by 2+ students: celebratory "✨ No systemic gaps"
 *   state (Expand disabled).
 * - When 1+ systemic gaps exist: shows count; user clicks Expand → cards list.
 *
 * Each gap card targets the AFFECTED STUDENTS ONLY (not the whole class) per
 * Q1.a — so we pass their enrollment_ids into RemedialAssessmentDialog.
 *
 * Props:
 *   groups: list of { concept_tag, count, students: [{name, child_id, mission_id}] }
 *           from /api/teacher/me/alerts/needs-help?assessment_id=...
 *   needsHelpByStudent: list of { enrollment_id, child_id, ... } from
 *           /api/teacher/me/assessments/{aid}/needs-help-by-student — used to
 *           map child_id → enrollment_id for audience scoping.
 *   assessmentId
 *   assessmentTitle
 *   defaultDurationMinutes
 *   onStudentClick: fn(student object from needsHelpByStudent) — opens the
 *                   per-student sheet for the affected student.
 *   onRemedialCreated: fn(newAid)
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Sparkles, Users } from 'lucide-react';
import RemedialAssessmentDialog from '@/components/RemedialAssessmentDialog';

export default function ClassWideGapsRollup({
  groups = [],
  needsHelpByStudent = [],
  assessmentId,
  assessmentTitle,
  defaultDurationMinutes = 20,
  onStudentClick,
  onRemedialCreated,
}) {
  const [expanded, setExpanded] = useState(false);
  const [remedialOpen, setRemedialOpen] = useState(false);
  const [remedialTag, setRemedialTag] = useState(null);
  const [remedialEnrollmentIds, setRemedialEnrollmentIds] = useState([]);
  const [remedialStudentLabel, setRemedialStudentLabel] = useState(null);

  // Filter to systemic gaps only (count >= 2).
  const systemicGaps = useMemo(
    () => (groups || []).filter((g) => (g.count || 0) >= 2),
    [groups],
  );
  const hasSystemic = systemicGaps.length > 0;

  // Build child_id -> enrollment_id map from per-student feed.
  const enrollmentByChild = useMemo(() => {
    const m = {};
    for (const s of needsHelpByStudent) {
      if (s.child_id && s.enrollment_id) m[s.child_id] = s.enrollment_id;
    }
    return m;
  }, [needsHelpByStudent]);
  // Build child_id -> student object for click-to-open
  const studentByChild = useMemo(() => {
    const m = {};
    for (const s of needsHelpByStudent) {
      if (s.child_id) m[s.child_id] = s;
    }
    return m;
  }, [needsHelpByStudent]);

  const openRemedial = (gap) => {
    const eids = (gap.students || [])
      .map((s) => enrollmentByChild[s.child_id])
      .filter(Boolean);
    setRemedialTag(gap.concept_tag);
    setRemedialEnrollmentIds(eids);
    setRemedialStudentLabel(`${eids.length} affected student${eids.length === 1 ? '' : 's'}`);
    setRemedialOpen(true);
  };

  return (
    <>
      <div
        className="mt-6 bg-white border rounded-xl overflow-hidden"
        data-testid="class-wide-gaps-rollup"
      >
        <button
          type="button"
          onClick={() => hasSystemic && setExpanded((v) => !v)}
          disabled={!hasSystemic}
          className={`w-full px-4 py-3 flex items-center justify-between gap-3 bg-slate-50 border-b transition-colors ${
            hasSystemic ? 'hover:bg-slate-100 cursor-pointer' : 'cursor-default'
          }`}
          data-testid="class-wide-gaps-toggle"
        >
          <div className="flex items-center gap-2 text-sm text-left">
            {hasSystemic ? (
              expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />
            ) : (
              <Sparkles size={14} className="text-emerald-500" />
            )}
            {hasSystemic ? (
              <span className="font-semibold text-slate-900">
                Class-wide gaps ·{' '}
                <span className="text-rose-600">
                  {systemicGaps.length} topic{systemicGaps.length === 1 ? '' : 's'}
                </span>{' '}
                flagged by 2+ students
              </span>
            ) : (
              <span
                className="font-semibold text-emerald-700"
                data-testid="class-wide-gaps-empty"
              >
                No systemic gaps — nice work
              </span>
            )}
          </div>
          {hasSystemic && (
            <Badge variant="outline" className="text-[10px]">
              {expanded ? 'Collapse' : 'Expand'}
            </Badge>
          )}
        </button>

        {hasSystemic && expanded && (
          <div className="p-4 space-y-3" data-testid="class-wide-gaps-body">
            {systemicGaps.map((g) => {
              const slug = (g.concept_tag || 'topic').replace(/\s+/g, '-').toLowerCase();
              return (
                <div
                  key={g.concept_tag}
                  className="border border-rose-200 bg-rose-50/40 rounded-lg p-3.5"
                  data-testid={`class-wide-gap-${slug}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    <span className="font-semibold text-slate-900">{g.concept_tag}</span>
                    <Badge variant="outline" className="ml-auto bg-rose-50 text-rose-700 border-rose-300 text-[10px]">
                      {g.count} student{g.count === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-rose-700 mb-2">Likely class-wide gap</p>
                  <p className="text-xs text-slate-700 mb-3">
                    Affected:{' '}
                    {(g.students || []).map((s, i) => {
                      const student = studentByChild[s.child_id];
                      return (
                        <span key={s.child_id}>
                          {i > 0 && ', '}
                          {student ? (
                            <button
                              type="button"
                              onClick={() => onStudentClick?.(student)}
                              className="text-indigo-600 hover:underline font-medium"
                              data-testid={`class-wide-gap-student-${slug}-${s.child_id}`}
                            >
                              {s.name}
                            </button>
                          ) : (
                            <span className="font-medium">{s.name}</span>
                          )}
                        </span>
                      );
                    })}
                  </p>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => openRemedial(g)}
                      data-testid={`class-wide-remedial-${slug}`}
                    >
                      <Sparkles size={13} className="mr-1.5" />
                      Generate remedial assessment
                      <Users size={11} className="ml-1.5 opacity-70" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Remedial dialog: pre-filled with the single concept tag + affected
          students audience */}
      <RemedialAssessmentDialog
        open={remedialOpen}
        onOpenChange={setRemedialOpen}
        sourceAid={assessmentId}
        sourceTitle={assessmentTitle}
        conceptTags={remedialTag ? [remedialTag] : []}
        defaultDurationMinutes={defaultDurationMinutes}
        targetEnrollmentIds={remedialEnrollmentIds}
        targetStudentLabel={remedialStudentLabel}
        onCreated={(newAid) => {
          setRemedialOpen(false);
          if (typeof onRemedialCreated === 'function') onRemedialCreated(newAid);
        }}
      />
    </>
  );
}
